<template>
  <div class="p-2">
    <div class="container-fluid">
      <div class="row header m-4">
        <div class="row">
          <h1>Episodic Payments</h1>
          <div v-if="paymentDistribution.status != null" class="episode-banner ml-3 mt-2"><h3>{{ episodeBanner(paymentDistribution.status) }}</h3></div>
        </div>
      </div>
      <div class="row patient-summary-header py-2">
        <div class="col-sm-3 mt-1">
          <h5>Patient Summary</h5>
        </div>
        <div v-if="paymentDistribution.bundle.durationDays >0" class="col-sm-4 mt-1 pr-5">
          <h5>
            <b-skeleton-wrapper :loading="isLoading">
              <template #loading>
                <b-skeleton width="65%"/>
              </template>
              {{ formatTerm(paymentDistribution.bundle.durationDays) + paymentDistribution.bundle.name }}
            </b-skeleton-wrapper>
          </h5>
        </div>
        <div v-else-if="paymentDistribution.bundle.durationDays === 0" class="col-sm-4 mt-1 pr-5">
          <h5>
            <b-skeleton-wrapper :loading="isLoading">
              <template #loading>
                <b-skeleton width="65%"/>
              </template>
              {{ formatTerm(paymentDistribution.bundle.durationDays) + paymentDistribution.bundle.name }}
            </b-skeleton-wrapper>
          </h5>
        </div>
        <div class="col-sm-3 mt-1">
          <h5>Episode Financial Summary</h5>
        </div>
        <div class="col-sm-2 mt-1 button-container">
          <h5>Episode Management</h5>
        </div>
      </div>
      <div class="row patient-summary py-2">
        <div class="col-sm-3">
          <div>
            <b-skeleton-wrapper :loading="isLoading">
            <template #loading>
              <span class="field-label">Patient: </span><b-skeleton width="33%" class="inline"></b-skeleton><br>
              <span class="field-label">Lead Physician: </span><b-skeleton width="35%" class="inline"></b-skeleton><br>
              <span class="field-label">Birth Date: </span><b-skeleton width="22%" class="inline"></b-skeleton><br>
              <span class="field-label">MRN: </span><b-skeleton width="25%" class="inline"></b-skeleton><br>
              <span class="field-label">Patient ID: </span><b-skeleton width="26%" class="inline"></b-skeleton><br>
              <span class="field-label">Phone: </span><b-skeleton width="20%" class="inline"></b-skeleton><br>
              <span class="field-label">E-Mail: </span><b-skeleton width="35%" class="inline"></b-skeleton><br>
            </template>

              <span class="field-label">Patient: </span>{{ paymentDistribution.patient.firstName + ' ' + paymentDistribution.patient.lastName }}<br>
              <span class="field-label">Lead Physician: </span>{{ paymentDistribution.estimate.providerName }}<br>
              <span class="field-label">Birth Date: </span>{{ formatBirthDate(paymentDistribution.patient.dob) }}<br>
              <span class="field-label">MRN: </span>{{ paymentDistribution.patient.mrn }}<br>
              <span class="field-label">Patient ID: </span>{{ paymentDistribution.patient.patientIdFromOrg }}<br>
              <span class="field-label">Phone: </span>{{ paymentDistribution.patient.phoneNumber }}<br>
              <span class="field-label">E-Mail: </span>{{ paymentDistribution.patient.email }}<br>
            </b-skeleton-wrapper>
          </div>
        </div>
        <div class="col-sm-4">
          <div>
            <b-skeleton-wrapper :loading="isLoading">
              <template #loading>
                <b-skeleton width="75%"></b-skeleton>
                <b-skeleton width="55%"></b-skeleton>
                <b-skeleton width="65%"></b-skeleton>
              </template>
              <span>
                <span v-if="paymentDistribution.bundle.durationDays >0">
                <span class="field-label ">Start Date: </span>{{formatDate(paymentDistribution.estimate.procedureDate)}}<br>
                  </span>
                <span v-if="paymentDistribution.bundle.durationDays === 0 && paymentDistribution.estimate.procedureDate">
                <span class="field-label ">Procedure Date:</span> {{formatDate(paymentDistribution.estimate.procedureDate)}} <br>
                  </span>
                <span class="field-label ">Episode Created Date: </span>{{formatDate(paymentDistribution.createdAt)}}<br>
                <span v-if="paymentDistribution.estimate && paymentDistribution.estimate.location" >
                <span class="field-label ">Location: </span>{{paymentDistribution.estimate.location.name}} <br>
                  </span>
                <span class="field-label ">Lead Code: </span>{{displayCode(paymentDistribution.bundle)}} <span></span> <br>
              </span>
              <span v-for="(code, index) in paymentDistribution.bundle.codes" :key="'code-' + index" v-if="paymentDistribution.bundle.codes">
                <span class="field-label ">Additional Code: </span>{{displayModifierCode(code)}}<br></span>
              <span class="mt-2"><span class="field-label ">Procedure Summary: </span>{{paymentDistribution.bundle.description}}</span>
            </b-skeleton-wrapper>
          </div>
        </div>
        <div class="col-sm-3">
          <div class="pb-2" v-if="servicesTotal > paymentDistribution.maxFundingAmount"><span class="pending-label">Allocated Total Exceeds Available Funding Amount</span></div>
          <div class="pb-2" v-if="paymentDistribution.fundingIsPending === true"><span class="pending-label">Funding In Progress</span>
          </div>
          <div class="financial-summary-container">
            <b-skeleton-wrapper :loading="isLoading">
              <div class="pr-2">
                <span class="field-label">Total Episodic Fee</span><br>
                <span v-if="paymentDistribution.estimate.patientAmount">
                  <span class="field-label">Patient Responsibility</span><br>
                <span v-if="paymentDistribution.estimate.patientTotal >0" class="field-label">Patient Amount Received<br></span>
                <span class="field-label">Payer Funding Amount</span><br>
                </span>
                <span class="total-description final-total mt-2">Episode Balance</span><br>
              </div>
              <div class="pl-3 text-right">
                <span>$ {{ formatNumber(paymentDistribution.price) }}</span><br>
                <span v-if="paymentDistribution.estimate.patientAmount">
                  <span>$ {{ formatNumber(paymentDistribution.estimate.patientTotal) }}</span><br>
                  <span v-if="paymentDistribution.estimate.patientTotal >0">$ {{ formatNumber(paymentDistribution.estimate.amountPaid) }}<br></span>
                  <span>$ {{ formatNumber(paymentDistribution.maxFundingAmount) }}</span><br>
                </span>
                <span class="total-value final-total mt-2">$ {{ formatNumber(paymentDistribution.availableFunds) }}</span><br>
              </div>
            </b-skeleton-wrapper>
          </div>
        </div>
        <div class="col-sm-2 button-container">
          <div v-if="paymentDistribution.employer && paymentDistribution.employer.employerFundingType === 'Direct'">
          <div class="mt-2" v-if="!isLoading && !paymentDistribution.isFullyFunded && paymentDistribution.status !== 'Closed'">
            <button @click.prevent="initiateFunding()" class="btn btn-outline-orange" :disabled="paymentDistribution.status !== 'Approved'">
              <font-awesome-icon class="orange mr-1" :icon="['far', 'file-invoice-dollar']"/>
              Initiate Funding
            </button></div>
          </div>
          <div class="mt-2" v-if="paymentDistribution.employer && paymentDistribution.employer.employerFundingType === 'Claim' && !paymentDistribution.isFullyFunded && paymentDistribution.status !== 'Closed'">
            <button v-if="isAdmin" @click.prevent="addFunds()" class="btn btn-outline-orange" :disabled="paymentDistribution.status !== 'Approved'">
              <font-awesome-icon class="orange mr-1" :icon="['far', 'file-invoice-dollar']"/>
              Add Funding
            </button></div>
          <div class="mt-2">
            <button v-if="paymentDistribution.employer && paymentDistribution.employer.employerFundingType === 'Claim'" @click.prevent="viewClaim()" class="btn btn-outline-orange" :disabled="!paymentDistribution.currentClaim">
              <font-awesome-icon class="orange mr-1" :icon="['fas', 'file-shield']"/>
              Review Claim
            </button></div>
          <div class="mt-2">
            <button v-if="paymentDistribution.documents && paymentDistribution.documents.length > 0" @click.prevent="viewFile()" class="btn btn-outline-orange">
              <font-awesome-icon class="orange mr-1" :icon="['fas', 'file-alt']"/>
              Review Files
            </button></div>
          <div class="mt-2">
            <button v-if="paymentDistribution.status !== 'Closed'" @click.prevent="closeEpisode()" class="btn btn-outline-orange">
              <font-awesome-icon class="orange mr-1" :icon="[ 'far', 'check']"/> Close Episode
            </button>
          </div>
        </div>
      </div>
      <div class="row mt-4">
        <div class="col">
          <h5>EPISODE FEE DISTRIBUTION</h5>
        </div>
      </div>
      <b-card-group deck>
        <div v-for="service in paymentDistribution.services" :key="service.organizationBundlePaymentDistributionServiceId">
          <template>
            <div class="w-100">
              <b-card header-tag="header"
                      border-variant="grey"
                      class="m-4"
                      style="width: 22rem">
                <template #header>
                  <div class="row">
                    <div class="col"><h5 class="mt-2 text-uppercase text-left">{{ service.bundleService.serviceType.name }}</h5></div>
                    <div><button v-if="paymentDistribution.status !== 'Closed'" title="Edit" @click.prevent="editService(service)" type="button" class="btn btn-outline-orange mx-2 text-right circle">
                      <font-awesome-icon class="orange" size="lg" :icon="['fal', 'pen']"/>
                    </button></div>
                  </div>
                </template>
                <b-card-text>
                  <div>
                    <div><span class="field-label">Allocated Total: </span> ${{ formatNumber(service.originalAmount) }}</div>
                    <hr>
                    <div v-if="serviceDisplayName(service)"><span class="field-label">Participating Provider: </span>{{ serviceDisplayName(service) }}</div>
                    <div v-if="service.bundleService.defaultPayee && service.bundleService.defaultPayee.taxId > 0"><span class="field-label">TIN: </span>{{ service.bundleService.defaultPayee.taxId }}</div>
                    <div v-if="service.bundleService.defaultPayee && service.bundleService.defaultPayee.npi > 0"><span class="field-label">NPI: </span>{{ service.bundleService.defaultPayee.npi }}</div>
                    <div v-if="service.originalAmount> 0" ><span class="field-label">Approve Payment: </span> ${{ formatNumber(service.originalAmount) }}</div>
                  </div>
                </b-card-text>
                <div v-if="service.transactions.length > 0">
                  <div class="header-caret" @click.prevent="toggleDetails(service)"><u class="mb-3">Amount Paid</u>
                    <font-awesome-icon size="lg" class="ml-1" :icon="['fas', 'caret-up']" v-show="!showingBundleTransactionDetails(service)"/>
                    <font-awesome-icon size="lg" class="ml-1" :icon="['fas', 'caret-down']" v-show="showingBundleTransactionDetails(service)"/>
                  </div>
                </div>
                  <transition name="slide">
                    <div v-show="showingBundleTransactionDetails(service)" class="mb-4">
                      <div v-for="transaction in service.transactions" :key="transaction.organizationBundlePaymentDistributionTransactionId">
                        <div v-if="transaction.status === 'Pending'" class="mb-1"><span class="pending-label">PAYMENT IS PENDING</span></div>
                        <span class="field-label">Prior Payment :</span>${{ formatNumber(transaction.amount) }} on {{formatDate(transaction.createdAt)}}<br>
                      </div>
                     <div  v-if="service.transactions.length > 0"> <span class="field-label">Balance: </span>${{ formatNumber(service.originalAmount + service.totalPaid) }}</div>
                    </div>
                  </transition>
                <div class="row justify-content-center">
                  <div v-if="paymentDistribution.status !== 'Closed'">
                  <button v-if="service.bundleService.defaultPayee !== null" @click.prevent="confirmServiceInformation(service)" :disabled="!paymentDistribution.availableFunds > 0" type="button" class="btn btn-orange"><font-awesome-icon class="mr-2" :icon="['far', 'message-dollar']"/>
                    Pay</button>
                  <button v-else @click.prevent="editService(service)" type="button" class="btn btn-orange">
                    Set Participating Provider</button>
                    </div>
                </div>
              </b-card>
            </div>
          </template>
        </div>
      </b-card-group>
    </div>
    <div class="col-2">
    </div>

    <fund-distribution-modal :payment-distribution="paymentDistribution" :visible="showingFundingModal" @update:visible="visible => showingFundingModal = visible" @fundingAdded="fundingAdded()"></fund-distribution-modal>
    <payment-distribution-add-funds-modal :payment-distribution="paymentDistribution" :visible="showingAddFundsModal" @update:visible="visible => showingAddFundsModal = visible" @fundsAdded="fundsAdded"></payment-distribution-add-funds-modal>
    <bundle-claim-modal :currentClaim="paymentDistribution.currentClaim" :orgPatientEstimateId="paymentDistribution.organizationPatientEstimateId" :visible="showingBundleClaimModal" @update:visible="visible => showingBundleClaimModal = visible" @claimSubmitted="claimSubmitted"></bundle-claim-modal>
    <pay-distribution-modal ref="payModal" :payment-distribution="paymentDistribution"
                            :payment-distribution-service="currentPaymentDistributionService"
                            :account="selectedAccount"
                            :bundle-service-id="currentPaymentDistributionService.bundleService.organizationBundleServiceId"
                            :bundle-id="paymentDistribution.bundle.organizationBundleId" :bundle-service-type-id="currentPaymentDistributionService.bundleService.serviceType.bundleServiceTypeId"
                            :distribution-amount="distributionAmount" :visible="showingPayModal" @update:visible="visible => showingPayModal = visible"
                            @paymentAdded="paymentAdded" @savePayment="savePayment"></pay-distribution-modal>
    <send-bundle-payment-modal @paymentConfirmed="paymentConfirmed" title="Send Payment"
                   :visible="showPaymentConfirmation"
                   :payment="pendingPayment"
                               :available-funds="paymentDistribution.availableFunds"
                   @update:visible="visible => showPaymentConfirmation = visible">
    </send-bundle-payment-modal>
    <close-episode-modal :visible="showingCloseModal"
                         :services="services"
                         :payment-distribution-id="paymentDistribution.organizationBundlePaymentDistributionId"
                         @updatePaymentSent="paymentSent"
                         :balance="availableFunds"
                         @update:visible="visible => showingCloseModal = visible">
                         </close-episode-modal>
    <view-file-modal :visible="showingViewFileModal" :payee-documents="paymentDistribution.documents" @update:visible="visible => showingViewFileModal = visible"></view-file-modal>
    <confirm-modal @confirmed="statusUpdated" class="align-middle"
                   confirmButton="Confirm"
                   title= "Close Episode"
                   :visible="showingConfirmModal"
                   @update:visible="visible => closeConfirmModal(visible)">
      <div class="row mb-3">
        <div class="col-2"></div>
        <div class="col-8 text-center">Please confirm the episode may be closed</div>
        <div class="col-2"></div>
      </div>
    </confirm-modal>
  </div>
</template>

<script>
  import api from '@/services/api'
  import moment from 'moment-timezone'
  import FundDistributionModal from '@/components/FundDistributionModal'
  import PayDistributionModal from '@/components/PayDistributionModal'
  import ErrorsMixin from '@/mixins/ErrorsMixin'
  import SendBundlePaymentModal from '@/components/SendBundlePaymentModal'
  import CloseEpisodeModal from '@/components/CloseEpisodeModal'
  import _ from 'lodash'
  import ConfirmModal from '@/components/ConfirmModal'
  import ViewFileModal from '@/components/ViewFileModal'
  import BundleClaimModal from '@/components/BundleClaimModal.vue'
  import PaymentDistributionAddFundsModal from '@/components/PaymentDistributionAddFundsModal.vue'

  export default {
    name: 'BundlePaymentDistribution',

    mixins: [ErrorsMixin],

    components: { PaymentDistributionAddFundsModal, BundleClaimModal, ViewFileModal, FundDistributionModal, PayDistributionModal, SendBundlePaymentModal, CloseEpisodeModal, ConfirmModal },

    props: {
      id: {
        default: 0
      }
    },

    data () {
      return {
        isLoading: false,
        hidingServiceDetails: [],
        showingFundingModal: false,
        showingAddFundsModal: false,
        showingConfirmModal: false,
        showingPayModal: false,
        showingCloseModal: false,
        showPaymentConfirmation: false,
        showingTransactionDetails: {},
        showingPaymentDetails: true,
        showingViewFileModal: false,
        showingBundleClaimModal: false,
        currentClaim: {},
        paymentDistribution: {
          organizationBundlePaymentDistributionId: 0,
          patient: {
            firstName: '',
            lastName: '',
            imageUrl: 'https://s3.amazonaws.com/health-here-assets/Images/PhotoPlaceholder.jpg'
          },
          bundle: {
            organizationBundleId: 0,
            name: '',
            description: ''
          },
          estimate: {
            providerName: ''
          }
        },
        services: [],
        availableFunds: 0,
        paymentDistributionStatus: '',
        currentPaymentDistributionService: {
          bundleService: {
            serviceType: {
              bundleServiceTypeId: 0
            }
          }
        },

        selectedAccount: {},
        pendingPayment: {
          pendingAmount: 0,
          pendingAccount: {
            last4: 0,
            accountName: ''
          },
          payee: {
            organizationPayeeId: 0,
            businessName: '',
            firstName: '',
            lastName: ''
          },
          npi: 0,
          taxId: 0
        },
        distributionAmount: 0
      }
    },

    watch: {
      // call again the method if the route changes
      '$props.id': 'load',
      'paymentDistribution.services' (newValue) {
        this.services = _.clone(newValue)
      },
      'paymentDistribution.availableFunds' (newValue) {
        this.availableFunds = _.clone(newValue)
      }
    },
    computed: {
      servicesTotal () {
        let sum = 0
        const services = this.paymentDistribution?.services || []
        services.forEach(service => {
          sum += service.amount
        })
        return sum
      },

      isAdmin () {
        return this.$store.isAdmin
      }
    },
    methods: {
      fundingAdded () {
        this.showingFundingModal = false
        this.load()
      },

      fundsAdded () {
        this.showingAddFundsModal = false
        this.load()
      },

      claimSubmitted () {
        this.showingBundleClaimModal = false
        this.load()
      },

      load () {
        this.isLoading = true
        api.get('/Dashboard/PaymentDistribution/' + this.id).then(response => {

          this.paymentDistribution = response.data
          if (!this.paymentDistribution.patient.imageUrl) {
            this.paymentDistribution.patient.imageUrl = 'https://s3.amazonaws.com/health-here-assets/Images/PhotoPlaceholder.jpg'
          }
          this.isLoading = false
        }).catch(error => {
          this.isLoading = false
          if (!this.$store.canHandleNetworkError(error)) {
            this.$log.error('Error attempting to load patient details: ' + error.message, error)
          }
        })
      },

      statusUpdated (confirmed) {
        this.showingConfirmModal = false
        if (confirmed === false) {
          this.showingConfirmModal = false
          return
        }
        api.post('/Dashboard/PaymentDistribution/' + this.paymentDistribution.organizationBundlePaymentDistributionId + '/Close').then(() => {
          this.load()
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.setFromApiErrorResponse(error)
            this.$log.error('Error attempting to load payees: ' + error.message, error)
          }
          this.isSaving = false
        })
      },

      serviceDisplayName (service) {
        if (service.bundleService.defaultPayee === null) {
          return ''
        }

        if (service.bundleService.defaultPayee.businessName) {
          return service.bundleService.defaultPayee.businessName
        }

        let displayName = ''

        if (service.bundleService.defaultPayee.users.length >= 1) {
          const payee = service.bundleService.defaultPayee.users[0].user
          if (payee.firstName) {
            displayName += payee.firstName
          }

          if (payee.lastName) {
            displayName += ' ' + payee.lastName
          }
        }

        return displayName
      },

      initiateFunding () {
        this.showingFundingModal = true
      },

      addFunds () {
        this.showingAddFundsModal = true
      },

      paymentAdded () {
        this.load()
        this.showingPayModal = false
      },

      closeEpisode () {
        if (this.paymentDistribution.availableFunds > 0) {
          this.showingCloseModal = true
          this.showingConfirmModal = false
        } else {
          this.showingCloseModal = false
          this.showingConfirmModal = true
        }
      },

      paymentSent () {
        this.load()
        this.showingCloseModal = false
      },

      editService (service) {
        this.currentPaymentDistributionService = service
        this.distributionAmount = service.originalAmount
        this.selectedAccount = service.bundleService.defaultPayeeAccount
        this.showingPayModal = true
      },

      confirmServiceInformation (service) {
        this.pendingPayment.pendingAmount = service.originalAmount
        this.pendingPayment.pendingAccount = service.bundleService.defaultPayeeAccount
        this.pendingPayment.payee = service.bundleService.defaultPayee
        this.pendingPayment.organizationBundlePaymentDistributionServiceId = service.organizationBundlePaymentDistributionServiceId
        this.pendingPayment.organizationBundlePaymentDistributionId = service.organizationBundlePaymentDistributionId

        this.showPaymentConfirmation = true
      },

      savePayment (payment) {
        this.showPaymentConfirmation = true
      },

      paymentConfirmed () {
        this.showPaymentConfirmation = false
        this.load()
      },

      formatBirthDate (dob) {
        if (dob) {
          return moment(dob).utc().format('MM/DD/YYYY')
        } else {
          return ''
        }
      },

      formatDate (date) {
        if (date) {
          return moment(date).utc().format('MM/DD/YYYY')
        } else {
          return ''
        }
      },

      formatNumber (number) {
        if (number === 0 || number === 0.0) {
          return '0.00'
        }
        if (number) {
          return number.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
        }

        return ''
      },

      episodeBanner (status) {
        if (status === 'Approved') {
          return 'Employer / TPA Payment Has Been Approved'
        }
        if (status === 'Pending') {
          return 'Pending Employer / TPA Payment Approval'
        }
        if (status === 'Closed') {
          return 'Episode Is Closed'
        }
        if (status === 'Denied') {
          return 'Episode Has Been Denied'
        }
      },

      formatTerm (days) {
        if (days === 0) {
          return 'DOS '
        } else {
          return days + 'D '
        }
      },

      toggleDetails (service) {
        this.$set(this.hidingServiceDetails, service.organizationBundlePaymentDistributionServiceId, !this.hidingServiceDetails[service.organizationBundlePaymentDistributionServiceId])
      },

      closeConfirmModal (visible) {
        if (!visible) {
          this.showingConfirmModal = false
        }
      },

      viewFile () {
        this.showingViewFileModal = true
      },

      viewClaim () {
        this.showingBundleClaimModal = true
        this.currentClaim =
          {
            controlNumber: '',
            correlationId: '',
            createdAt: '',
            requestFilePath: '',
            responseFilePath: '',
            rhClaimNumber: '',
            status: '',
            traceId: '',
            updatedAt: ''
          }
      },
      showingBundleTransactionDetails (service) {
        return !this.hidingServiceDetails[service.organizationBundlePaymentDistributionServiceId]
      },
      displayCode (bundle) {
        if (bundle.modifier) {
          return bundle.code + '-' + bundle.modifier
        }

        return bundle.code
      },
      displayModifierCode (code) {
        if (code) {
          return code.modifier
            ? `${code.code}-${code.modifier}`
            : code.code
        }
      }
    },
    created () {
      this.load()
    }
  }
</script>

<style scoped>
.patient-summary-header {
  border-top: 1px solid #FFA800;
  background-color: #f9f9f9;
}

h5 {
  color: #001859;
}

.patient-summary .column {
  padding: 0.75rem;
  vertical-align: bottom;
  border-collapse: collapse;
}

.patient-summary {
  border-bottom: 1px solid #dee2e6;
  background-color: #f9f9f9;
  /*cursor: pointer;*/
}

.header-row {
  padding: 0.75rem;
  vertical-align: bottom;
  border-top: 1px solid #dee2e6;
  border-bottom: 2px solid #dee2e6;
  font-weight: bold;
  border-collapse: collapse;
}

.data-row {
  min-height: 60px;
}

.total-description {
  display: inline-block;
  color: #2A84F4;
  font-weight: bolder;
}

.total-override {
  font-weight: bolder;
}

.final-total {
  font-weight: 700;
}

.total-value {
  display: inline-block;
}

.distribution-total {
  font-size: 1.2em !important;
  font-weight: 700;
}

.b-skeleton-text.inline {
  display: inline-block;
  vertical-align: middle;
}

.pay-button {
  min-width: 160px;
}

.card {
  font-size: 1.2em;
}

.confirm-label {
  color: #737284;
}

.dropdown-toggle {
  cursor: pointer;
}

.circle {
  border-radius: 50%;
  width: 40px;
  height: 40px;
  padding-left: 0.6rem;
  padding-right: 0.5rem;
}

h3{
  font-size: 18px;
  color: #001859;
  font-style: italic;
}

.service-icon{
  color: rgb(255, 70, 0);
}

.form-group {
  margin-bottom: 0px;
}

.pending-label {
  color: rgb(255, 70, 0);
  font-style: italic;
}

.header-caret {
  cursor: pointer;
  display: flex;
}

.financial-summary-container {
  display: flex;
}
.button-container {
  padding-left: 50px;
}
</style>
